import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { createPortal } from "react-dom";
import { nanoid } from "nanoid";
import {
  type CSSProperties,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { useAdminSpaceView } from "../hooks/useAdminSpaceView";
import { useAdminViewSubject } from "../hooks/useAdminViewSubject";
import { useHasAccess } from "../hooks/useHasAccess";
import { useNavTransition } from "../hooks/useNavTransition";
import { useQuery } from "@rocicorp/zero/react";
import { useScrollFade } from "../hooks/useScrollFade";
import type { DisplayableSpace } from "../hooks/useSpaceDisplayName";
import { CircleMesh } from "./CircleMesh";
import { parseBadgeConfig } from "./CircleMeshEditor";
import { BackPill, BackPillIcon, traverseBackPast } from "./BackPill";
import { WorkflowNavigationButton } from "./WorkflowNavigationButton";
import { WorkflowConnectorControl } from "./WorkflowConnectorControl";
import { WorkflowFeatureButton } from "./WorkflowFeatureButton";
import { Icon } from "./Icon";
import { Skeleton } from "./Skeleton";
import { SmallWorkflowBadge } from "./SmallWorkflowBadge";
import { Container } from "./Navigation";
import { SpaceBadge } from "./SpaceBadge";
import { Text } from "./Text";
import { TypewriterWorkflowName } from "./TypewriterWorkflowName";
import type { WorkflowData } from "./Workflow";

const BackRow = styled("div")`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  border-radius: 999px;
  background: var(--alpha4);
  & > a,
  & > a:hover,
  & > a:focus-visible {
    min-width: 0;
    background: transparent;
  }
  &:has(> a:hover),
  &:has(> a:focus-visible) {
    background: var(--alpha8);
  }
`;

const SpaceSlot = styled("span")`
  display: flex;
  flex-shrink: 0;
  padding-right: 6px;
`;

const FeatureButtons = styled("div")`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: 4px;
  margin-top: 2px;
  margin-bottom: 4px;
`;

export function WorkflowNavigationTitle({
  workflow,
}: {
  workflow: Pick<WorkflowData, "id" | "name" | "badgeSeed" | "pausedAt">;
}) {
  const badge = parseBadgeConfig(workflow.badgeSeed, workflow.id);
  return (
    <WorkflowNavigationButton
      data-workflow-menu
      data-no-pill
      aria-label={`Workflow options for ${workflow.name}`}
      aria-haspopup="menu"
    >
      <span
        data-workflow-badge
        style={{
          width: 16,
          height: 16,
          flexShrink: 0,
          borderRadius: 4,
          overflow: "hidden",
        }}
      >
        <CircleMesh
          key={workflow.badgeSeed}
          targetCircleSize={8}
          seed={badge.seed}
          mutingIntensity={0}
          animate={false}
          restrictedColors={badge.colors}
          grayscale={workflow.pausedAt != null}
        />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <Text ellipsis ellipsisTooltip={workflow.name}>
          <TypewriterWorkflowName name={workflow.name} />
        </Text>
      </span>
      <Icon
        name="chevron-down"
        size={12}
        color="var(--text-secondary)"
        style={{ flexShrink: 0 }}
      />
    </WorkflowNavigationButton>
  );
}

export function WorkflowBackRowSkeleton() {
  return <Skeleton style={{ width: "100%", height: 32 }} />;
}

export function WorkflowNavigationSkeleton() {
  const navEnter = useNavTransition("workflow");
  return (
    <Container aria-hidden data-nav-enter={navEnter}>
      <Skeleton style={{ height: 32, flexShrink: 0 }} />
    </Container>
  );
}

const MAX_SPACE_BADGE_SHARE = 0.3;

// Collapses the space badge only when it would crowd a workflow name that doesn’t fit beside it.
function useCollapsedSpaceBadge() {
  const [row, setRow] = useState<HTMLDivElement | null>(null);
  const [name, setName] = useState<HTMLSpanElement | null>(null);
  const [space, setSpace] = useState<HTMLSpanElement | null>(null);
  const expandedSpaceWidth = useRef(0);
  const [collapsed, setCollapsed] = useState(false);

  useLayoutEffect(() => {
    const pill = name?.closest("a");
    if (!row || !name || !space || !pill) return;
    const update = () => {
      if (!collapsed) expandedSpaceWidth.current = space.offsetWidth;
      const nameEnd =
        name.getBoundingClientRect().left -
        row.getBoundingClientRect().left +
        name.scrollWidth +
        parseFloat(getComputedStyle(pill).paddingRight);
      const width = row.clientWidth;
      setCollapsed(
        nameEnd + expandedSpaceWidth.current > width &&
          expandedSpaceWidth.current > width * MAX_SPACE_BADGE_SHARE
      );
    };
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(row);
    resizeObserver.observe(space);
    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(name, {
      characterData: true,
      childList: true,
      subtree: true,
    });
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [row, name, space, collapsed]);

  return { setRow, setName, setSpace, collapsed };
}

export function WorkflowNavigation({
  workflow,
  isReadOnly = false,
  menu,
  backHref,
  headerSlot,
  optionsMenu,
  children,
}: {
  workflow: Pick<
    WorkflowData,
    | "id"
    | "name"
    | "spaceId"
    | "autofixEnabled"
    | "autoImproveEnabled"
    | "badgeSeed"
    | "pausedAt"
  > & { space?: DisplayableSpace };
  isReadOnly?: boolean;
  menu: ReactNode;
  // Replaces the title menu with a back link, moving the menu into the feature buttons.
  backHref?: string;
  // Where the back row goes, in place of the Home and Chat switch.
  headerSlot?: HTMLElement | null;
  optionsMenu?: (target: string) => ReactNode;
  children: ReactNode;
}) {
  const z = useZero();
  const hasWriteAccess = useHasAccess("space:write", workflow.spaceId);
  const canWrite = hasWriteAccess && !isReadOnly;
  const canCreateConnector = useHasAccess("tenant:connected_app_create");
  const [connector, connectorResult] = useQuery(
    queries.connectedAppForWorkflow({ workflowId: workflow.id })
  );
  const showConnector = !!connector || (hasWriteAccess && canCreateConnector);
  const navEnter = useNavTransition("workflow");
  const adminSubject = useAdminViewSubject();
  const adminSpace = useAdminSpaceView();
  const watchtower = !!adminSubject || !!adminSpace;
  const [featureAnchorName] = useState(() => `--workflow-features-${nanoid()}`);
  const scrollRef = useRef<HTMLDivElement>(null);
  useScrollFade({ scrollRef });
  const {
    setRow: setBackRow,
    setName: setBackName,
    setSpace: setSpaceSlot,
    collapsed: spaceCollapsed,
  } = useCollapsedSpaceBadge();
  const automationControls = [
    {
      label: "Autofix",
      icon: "radar" as const,
      isOn: workflow.autofixEnabled ?? true,
      description:
        "When enabled, 3B monitors for errors and automatically creates a fix branch.",
      onToggle: () => {
        if (!canWrite) return;
        z.mutate(sharedMutators.toggleAutofix({ workflowId: workflow.id }));
      },
    },
    {
      label: "Autotune",
      icon: "audio-waveform" as const,
      isOn: workflow.autoImproveEnabled ?? false,
      description:
        "When enabled, 3B continuously looks for ways to make this workflow more reliable, faster, or more capable, and proposes them on a branch.",
      onToggle: () => {
        if (!canWrite) return;
        z.mutate(sharedMutators.toggleAutoImprove({ workflowId: workflow.id }));
      },
    },
  ];
  const backRow = backHref && (
    <BackRow ref={setBackRow}>
      <BackPill
        aria-label={`Back from ${workflow.name}`}
        to={backHref}
        onMouseDown={(event) => {
          if (backHref.includes("/workflows/")) return;
          traverseBackPast(event, (url) =>
            url.pathname.endsWith(`/workflows/${workflow.id}`)
          );
        }}
      >
        <BackPillIcon>
          {watchtower ? (
            <Icon name="tower-control" size={16} />
          ) : (
            <SmallWorkflowBadge workflow={workflow} />
          )}
        </BackPillIcon>
        <Text ref={setBackName} ellipsis ellipsisTooltip={workflow.name}>
          <TypewriterWorkflowName name={workflow.name} />
        </Text>
      </BackPill>
      {workflow.space && (
        <SpaceSlot ref={setSpaceSlot}>
          <SpaceBadge
            space={workflow.space}
            asLink
            collapsed={spaceCollapsed}
          />
        </SpaceSlot>
      )}
    </BackRow>
  );
  return (
    <Container
      ref={scrollRef}
      role="navigation"
      aria-label="Workflow navigation"
      data-nav-enter={navEnter}
    >
      {backHref ? headerSlot && createPortal(backRow, headerSlot) : menu}
      <FeatureButtons
        style={
          {
            anchorName: featureAnchorName,
            marginTop: backHref ? 0 : undefined,
            marginBottom: backHref ? -2 : undefined,
          } as CSSProperties
        }
      >
        {automationControls.map(
          ({ label, icon, isOn, description, onToggle }) => (
            <WorkflowFeatureButton
              key={label}
              target={featureAnchorName}
              label={label}
              icon={icon}
              isOn={isOn}
              sections={[
                {
                  items: [
                    {
                      type: "button",
                      left: null,
                      label,
                      subtitle: (
                        <Text
                          variant="text-small"
                          color="var(--text-secondary)"
                          style={{ display: "block" }}
                        >
                          {description}
                        </Text>
                      ),
                      toggled: isOn,
                      disabled: !canWrite,
                      onClick: onToggle,
                      keepOpen: true,
                    },
                  ],
                },
              ]}
            />
          )
        )}
        {showConnector && (
          <WorkflowConnectorControl
            target={featureAnchorName}
            workflow={workflow}
            isReadOnly={isReadOnly}
            connector={connector}
            isLoaded={connectorResult.type === "complete"}
          />
        )}
        {backHref && optionsMenu?.(featureAnchorName)}
      </FeatureButtons>
      {children}
    </Container>
  );
}
